import React from 'react';
import { CrawlFbResponse, CrawlFbStatus } from '../../../../api/crawlFb';
import PendingStatus from './StatusSteps/PendingStatus';
import ProcessingStatus from './StatusSteps/ProcessingStatus';
import CompletedStatus from './StatusSteps/CompletedStatus';
import FailedStatus from './StatusSteps/FailedStatus';

interface CrawlStatusDisplayProps {
  crawlStatus: CrawlFbResponse | undefined;
  isPolling: boolean;
  onNewCrawl: () => void;
  isShowNewCrawl: boolean;
  onImageSelect?: (imageData: { imageUrl: string; title: string }) => void;
}

const CrawlStatusDisplay: React.FC<CrawlStatusDisplayProps> = ({
  crawlStatus,
  isPolling,
  onNewCrawl,
  isShowNewCrawl = true,
  onImageSelect,
}) => {
  return (
    <div className="mt-8 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 rounded-3xl p-8 border border-slate-200/50 shadow-2xl hover:shadow-3xl transition-all duration-500 backdrop-blur-sm">
      <div className="flex items-center mb-8">
        <div className="w-14 h-14 bg-gradient-to-r from-blue-500 via-indigo-600 to-purple-700 rounded-2xl flex items-center justify-center shadow-xl mr-4">
          <svg
            className="w-7 h-7 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            ></path>
          </svg>
        </div>
        <div>
          <h3 className="text-2xl font-bold text-gray-800 mb-1">
            Trạng thái Crawl
          </h3>
          <p className="text-sm text-gray-600">
            Theo dõi tiến trình thu thập dữ liệu Facebook
          </p>
        </div>
      </div>

      {/* Loading state while polling */}
      {isPolling && !crawlStatus && (
        <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-blue-50 rounded-2xl p-6 border border-blue-200/50 shadow-lg">
          <div className="flex items-center">
            <div className="relative">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg mr-4">
                <svg
                  className="animate-spin w-6 h-6 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full animate-pulse"></div>
            </div>
            <div>
              <h4 className="text-lg font-bold text-blue-800 mb-1">
                Đang kiểm tra trạng thái...
              </h4>
              <p className="text-sm text-blue-600">
                Đang kết nối với server để lấy thông tin crawl
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Show crawl info and status */}
      {crawlStatus && (
        <div className="space-y-8">
          {/* Basic Info */}
          <div className="bg-white/90 backdrop-blur rounded-2xl p-6 border border-white/70 shadow-xl hover:shadow-2xl transition-all duration-300">
            <div className="flex items-center mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg mr-3">
                <svg
                  className="w-5 h-5 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
                </svg>
              </div>
              <span className="text-xl font-bold text-gray-800">
                Thông tin crawl
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="group">
                <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl p-5 border border-gray-200/50 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                  <div className="flex items-center mb-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-gray-500 to-slate-600 rounded-lg flex items-center justify-center mr-3">
                      <svg
                        className="w-4 h-4 text-white"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z" />
                      </svg>
                    </div>
                    <span className="text-gray-700 font-semibold text-sm uppercase tracking-wide">
                      🆔 ID Crawl
                    </span>
                  </div>
                  <div className="bg-white/80 rounded-xl p-3 font-mono text-sm text-gray-800 break-all border border-gray-100">
                    {crawlStatus.id}
                  </div>
                </div>
              </div>

              <div className="group">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-200/50 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                  <div className="flex items-center mb-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
                      <svg
                        className="w-4 h-4 text-white"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M16.36,14C16.44,13.34 16.5,12.68 16.5,12C16.5,11.32 16.44,10.66 16.36,10H19.74C19.9,10.64 20,11.31 20,12C20,12.69 19.9,13.36 19.74,14M14.59,19.56C15.19,18.45 15.65,17.25 15.97,16H18.92C17.96,17.65 16.43,18.93 14.59,19.56M14.34,14H9.66C9.56,13.34 9.5,12.68 9.5,12C9.5,11.32 9.56,10.65 9.66,10H14.34C14.43,10.65 14.5,11.32 14.5,12C14.5,12.68 14.43,13.34 14.34,14M12,19.96C11.17,18.76 10.5,17.43 10.09,16H13.91C13.5,17.43 12.83,18.76 12,19.96M8,8H5.08C6.03,6.34 7.57,5.06 9.4,4.44C8.8,5.55 8.35,6.75 8,8M5.08,16H8C8.35,17.25 8.8,18.45 9.4,19.56C7.57,18.93 6.03,17.65 5.08,16M4.26,14C4.1,13.36 4,12.69 4,12C4,11.31 4.1,10.64 4.26,10H7.64C7.56,10.66 7.5,11.32 7.5,12C7.5,12.68 7.56,13.34 7.64,14M12,4.03C12.83,5.23 13.5,6.57 13.91,8H10.09C10.5,6.57 11.17,5.23 12,4.03M18.92,8H15.97C15.65,6.75 15.19,5.55 14.59,4.44C16.43,5.07 17.96,6.34 18.92,8M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
                      </svg>
                    </div>
                    <span className="text-blue-700 font-semibold text-sm uppercase tracking-wide">
                      🔗 URL Facebook
                    </span>
                  </div>
                  <div className="bg-white/80 rounded-xl p-3 border border-blue-100">
                    <a
                      href={crawlStatus.urlCrawl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 font-medium text-sm break-all underline decoration-blue-300 hover:decoration-blue-500 transition-colors"
                    >
                      {crawlStatus.urlCrawl}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Status-specific components */}
          <div className="animate-in slide-in-from-bottom-4 duration-700">
            {crawlStatus.status === CrawlFbStatus.PENDING && <PendingStatus />}

            {crawlStatus.status === CrawlFbStatus.PROCESSING && (
              <ProcessingStatus crawlData={crawlStatus} />
            )}

            {crawlStatus.status === CrawlFbStatus.COMPLETED && (
              <CompletedStatus
                crawlData={crawlStatus}
                onNewCrawl={onNewCrawl}
                isShowNewCrawl={isShowNewCrawl}
                onImageSelect={onImageSelect}
              />
            )}

            {crawlStatus.status === CrawlFbStatus.FAILED && (
              <FailedStatus
                crawlData={crawlStatus}
                onRetry={onNewCrawl}
                isShowNewCrawl={isShowNewCrawl}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CrawlStatusDisplay;
